import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Handshake, Plus, Sparkles, Trash2, Loader2, GitCompare, Check } from "lucide-react";

const TYPES = ["Co-Publishing", "Administration", "Sub-Publishing", "Sync License", "Buyout"];
const STATUSES = ["Draft", "Negotiation", "Awaiting Signature", "In Review", "Signed", "Closed"];
const STATUS_COLOR = {
  Signed: "text-primary border-primary/40 bg-primary/10", Closed: "text-zinc-400 border-border bg-secondary",
  "Awaiting Signature": "text-amber-400 border-amber-400/40 bg-amber-400/10",
  Negotiation: "text-cyan-300 border-cyan-400/40 bg-cyan-400/10",
};
const EMPTY = { name: "", party: "", deal_type: "Co-Publishing", status: "Draft", value: 0, upfront: 0, royalty_rate: 0, term: "", territories: [], exclusivity: "Non-Exclusive", notes: "" };

export default function Deals() {
  const [deals, setDeals] = useState([]);
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [aiBusy, setAiBusy] = useState(null);

  const load = () => {
    api.get("/deals").then(({ data }) => setDeals(data)).catch(() => {});
    api.get("/deal-offers").then(({ data }) => setOffers(data.filter((o) => o.status === "Offered" || o.status === "Countered"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const respondOffer = async (id, action) => {
    let payload = { action };
    if (action === "counter") {
      const note = window.prompt("Counter terms / note to the publisher:");
      if (note === null) return;
      payload.counter_note = note;
    }
    try { await api.post(`/deal-offers/${id}/respond`, payload); toast.success(`Offer ${action}ed`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Deal name is required"); return; }
    try {
      await api.post("/deals", { ...form, value: +form.value, upfront: +form.upfront, royalty_rate: +form.royalty_rate,
        territories: (typeof form.territories === "string" ? form.territories.split(",").map((t) => t.trim()).filter(Boolean) : form.territories) });
      toast.success("Deal created");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/deals/${id}`); toast.success("Deal deleted"); load(); };
  const runAi = async (id) => {
    setAiBusy(id);
    try { const { data } = await api.post(`/deals/${id}/ai-summary`); setDeals((d) => d.map((x) => x.id === id ? { ...x, ai_summary: data.ai_summary } : x)); toast.success("AI summary ready"); }
    catch { toast.error("AI failed"); } finally { setAiBusy(null); }
  };

  return (
    <div data-testid="deals-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500 mb-2">Deal Room</h4>
          <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Deals</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-deal-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90"><Plus className="h-4 w-4 mr-2" />New Deal</Button></DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">New Deal</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
              <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Deal Name</Label><Input data-testid="deal-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-secondary border-border" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Counterparty</Label><Input data-testid="deal-party-input" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} className="mt-1.5 bg-secondary border-border" placeholder="e.g. Sony Music Publishing" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Type</Label>
                  <Select value={form.deal_type} onValueChange={(v) => setForm({ ...form, deal_type: v })}><SelectTrigger data-testid="deal-type-select" className="mt-1.5 bg-secondary border-border"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="deal-status-select" className="mt-1.5 bg-secondary border-border"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Value $</Label><Input type="number" data-testid="deal-value-input" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1.5 bg-secondary border-border font-mono" /></div>
                <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Upfront $</Label><Input type="number" value={form.upfront} onChange={(e) => setForm({ ...form, upfront: e.target.value })} className="mt-1.5 bg-secondary border-border font-mono" /></div>
                <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Rate %</Label><Input type="number" value={form.royalty_rate} onChange={(e) => setForm({ ...form, royalty_rate: e.target.value })} className="mt-1.5 bg-secondary border-border font-mono" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Term</Label><Input value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} className="mt-1.5 bg-secondary border-border" placeholder="e.g. 3 years" /></div>
                <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Territories</Label><Input value={form.territories} onChange={(e) => setForm({ ...form, territories: e.target.value })} className="mt-1.5 bg-secondary border-border" placeholder="US, UK, Worldwide" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-deal-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">Create Deal</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {offers.length > 0 && (
        <div className="mb-8" data-testid="incoming-offers">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />Publishing Offers for You</h3>
            {offers.length >= 2 && (
              <Button size="sm" data-testid="compare-offers-btn" onClick={() => setCompareOpen(true)} className="border-primary/40 bg-primary/10 text-primary hover:bg-primary/20" variant="outline">
                <GitCompare className="h-4 w-4 mr-2" />Compare {offers.length} offers
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {offers.map((o) => (
              <div key={o.id} data-testid={`incoming-offer-${o.id}`} className="bg-card border border-primary/40 rounded-lg p-6 row-in">
                <div className="flex items-start justify-between mb-3">
                  <div><div className="font-heading text-lg font-semibold">{o.name}</div><div className="text-sm text-zinc-500">from {o.party} · {o.deal_type}</div></div>
                  <span className="text-xs px-2 py-1 rounded border border-primary/40 bg-primary/10 text-primary font-semibold">{o.status === "Countered" ? "Countered" : "New Offer"}</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div><div className="font-mono text-lg">${(o.value || 0).toLocaleString()}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Value</div></div>
                  <div><div className="font-mono text-lg">${(o.upfront || 0).toLocaleString()}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Upfront</div></div>
                  <div><div className="font-mono text-lg">{o.royalty_rate}%</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Rate</div></div>
                </div>
                {o.territories?.length > 0 && <div className="text-xs text-zinc-400 mb-3">Territories: {o.territories.join(", ")} · Term: {o.term || "—"}</div>}
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`accept-offer-${o.id}`} onClick={() => respondOffer(o.id, "accept")} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">Accept</Button>
                  <Button size="sm" variant="outline" data-testid={`counter-offer-${o.id}`} onClick={() => respondOffer(o.id, "counter")} className="border-amber-400/40 bg-amber-400/10 text-amber-400 hover:bg-amber-400/20">Counter</Button>
                  <Button size="sm" variant="outline" data-testid={`decline-offer-${o.id}`} onClick={() => respondOffer(o.id, "decline")} className="border-border bg-secondary hover:bg-zinc-800 text-zinc-300">Decline</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {deals.length === 0 ? (
        <div className="bg-card border border-border rounded-lg text-center py-16"><Handshake className="h-10 w-10 text-zinc-700 mx-auto mb-4" /><p className="text-zinc-500">No deals yet.</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {deals.map((d) => (
            <div key={d.id} data-testid={`deal-card-${d.id}`} className="bg-card border border-border rounded-lg p-6 row-in">
              <div className="flex items-start justify-between mb-3">
                <div><div className="font-heading text-lg font-semibold">{d.name}</div><div className="text-sm text-zinc-500">{d.party || "—"} · {d.deal_type}</div></div>
                <span className={`text-xs px-2 py-1 rounded border font-semibold ${STATUS_COLOR[d.status] || "text-zinc-400 border-border bg-secondary"}`}>{d.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-3 my-4">
                <div><div className="font-mono text-lg">${(d.value || 0).toLocaleString()}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Value</div></div>
                <div><div className="font-mono text-lg">${(d.upfront || 0).toLocaleString()}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Upfront</div></div>
                <div><div className="font-mono text-lg">{d.royalty_rate || 0}%</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Rate</div></div>
              </div>
              {d.territories?.length > 0 && <div className="text-xs text-zinc-400 mb-3">Territories: {d.territories.join(", ")}</div>}
              {d.ai_summary && <div className="text-sm text-zinc-300 whitespace-pre-wrap bg-secondary/50 border border-border rounded-md p-3 mb-3" data-testid={`deal-ai-${d.id}`}>{d.ai_summary}</div>}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" data-testid={`deal-ai-btn-${d.id}`} onClick={() => runAi(d.id)} disabled={aiBusy === d.id} className="border-primary/40 bg-primary/10 text-primary hover:bg-primary/20">
                  {aiBusy === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />AI Summary</>}
                </Button>
                <button onClick={() => remove(d.id)} data-testid={`delete-deal-${d.id}`} className="text-zinc-500 hover:text-red-400 p-2 ml-auto"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="bg-card border-border max-w-4xl">
          <DialogHeader><DialogTitle className="font-heading">Compare Offers</DialogTitle></DialogHeader>
          {(() => {
            const maxVal = Math.max(0, ...offers.map((o) => o.value || 0));
            const maxUp = Math.max(0, ...offers.map((o) => o.upfront || 0));
            const maxRate = Math.max(0, ...offers.map((o) => o.royalty_rate || 0));
            const rows = [
              ["Publisher", (o) => o.party || "—", null],
              ["Type", (o) => o.deal_type, null],
              ["Value", (o) => `$${(o.value || 0).toLocaleString()}`, (o) => maxVal > 0 && (o.value || 0) === maxVal],
              ["Upfront", (o) => `$${(o.upfront || 0).toLocaleString()}`, (o) => maxUp > 0 && (o.upfront || 0) === maxUp],
              ["Royalty Rate", (o) => `${o.royalty_rate || 0}%`, (o) => maxRate > 0 && (o.royalty_rate || 0) === maxRate],
              ["Term", (o) => o.term || "—", null],
              ["Territories", (o) => (o.territories || []).join(", ") || "—", null],
            ];
            return (
              <div className="overflow-x-auto" data-testid="offer-compare-table">
                <table className="w-full border-collapse text-sm">
                  <thead><tr><th className="text-left p-3"></th>{offers.map((o) => <th key={o.id} className="text-left p-3 font-heading font-semibold">{o.name}</th>)}</tr></thead>
                  <tbody>
                    {rows.map(([label, get, best], ri) => (
                      <tr key={ri} className="border-t border-border">
                        <td className="p-3 text-zinc-500 uppercase text-xs tracking-widest whitespace-nowrap">{label}</td>
                        {offers.map((o) => {
                          const isBest = best && best(o);
                          return <td key={o.id} className={`p-3 font-mono ${isBest ? "text-primary font-semibold" : "text-zinc-300"}`}>{isBest && <Check className="h-3.5 w-3.5 inline mr-1" />}{get(o)}</td>;
                        })}
                      </tr>
                    ))}
                    <tr className="border-t border-border">
                      <td className="p-3"></td>
                      {offers.map((o) => <td key={o.id} className="p-3"><Button size="sm" data-testid={`compare-accept-${o.id}`} onClick={() => { setCompareOpen(false); respondOffer(o.id, "accept"); }} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">Accept</Button></td>)}
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
